import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import houseKeeping from "../assets/images/House.jpg";
import pestControl from "../assets/images/Pest.jpg";
import landscaping from "../assets/images/Garden.jpg";
import poolCleaning from "../assets/images/pool.jpg";
import fireAlarm from "../assets/images/firealarm.jpg";
import plumbing from "../assets/images/plumbering.jpg";
import electrical from "../assets/images/electrical.jpg";
import ServiceModal from "./ServiceModal";
import LazyImage from "./LazyImage";

const serviceData = [
    {
        id: 1,
        title: "HOUSE KEEPING & HYGIENE",
        description:
            "Professional cleaning and maintenance services for pristine environments.",
        image: houseKeeping,
    },
    {
        id: 2,
        title: "PEST CONTROL",
        description:
            "Comprehensive pest management solutions for all types of properties.",
        image: pestControl,
    },
    {
        id: 3,
        title: "LANDSCAPING & GARDEN MAINTENANCE",
        description:
            "Expert landscaping services to enhance your outdoor spaces.",
        image: landscaping,
    },
    {
        id: 4,
        title: "POOL CLEANING",
        description: "Professional pool maintenance for crystal clear waters.",
        image: poolCleaning,
    },

    {
        id: 5,
        title: "FIRE ALARM & FIRE FIGHTING",
        description:
            "Complete heating, ventilation, and air conditioning services.",
        image: fireAlarm,
    },
    {
        id: 6,
        title: "PLUMBING SERVICES",
        description: "Expert plumbing maintenance and repair solutions.",
        image: plumbing,
    },
    {
        id: 7,
        title: "ELECTRICAL SERVICES",
        description: "Professional electrical maintenance and installations.",
        image: electrical,
    },
];

const ServiceCard = ({ title, description, image, index, id }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg overflow-hidden shadow-lg min-w-[300px] snap-center"
            >
                <div className="h-48 overflow-hidden">
                    <LazyImage
                        src={image}
                        alt={`${title} services in Sri Lanka - Golden Eagle Management Solutions`}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                        width={768}
                        height={192}
                    />
                </div>
                <div className="p-6">
                    <h3 className="text-xl font-bold mb-3">{title}</h3>
                    <p className="text-gray-600 mb-4">{description}</p>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="text-primary hover:underline"
                    >
                        See more...
                    </button>
                </div>
            </motion.div>

            <ServiceModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                serviceId={id}
            />
        </>
    );
};

const Services = () => {
    const scrollContainerRef = useRef(null);
    const autoplayRef = useRef(null);
    const isPausedRef = useRef(false);

    const getChildren = () => {
        const container = scrollContainerRef.current;
        if (!container) return [];
        return Array.from(container.children || []);
    };

    const getNearestIndex = () => {
        const container = scrollContainerRef.current;
        const children = getChildren();
        if (!container || children.length === 0) return 0;
        const { scrollLeft } = container;
        let nearest = 0;
        let minDelta = Infinity;
        children.forEach((child, idx) => {
            const delta = Math.abs(child.offsetLeft - scrollLeft);
            if (delta < minDelta) {
                minDelta = delta;
                nearest = idx;
            }
        });
        return nearest;
    };

    const scrollToIndex = (index) => {
        const container = scrollContainerRef.current;
        const children = getChildren();
        if (!container || children.length === 0) return;
        const target = Math.max(0, Math.min(index, children.length - 1));
        const left = children[target].offsetLeft;
        container.scrollTo({ left, behavior: "smooth" });
    };

    const scrollByCards = (direction) => {
        const current = getNearestIndex();
        const children = getChildren();
        if (children.length === 0) return;
        const next =
            direction === "left"
                ? Math.max(0, current - 1)
                : Math.min(children.length - 1, current + 1);
        scrollToIndex(next);
    };

    const startAutoplay = () => {
        stopAutoplay();
        const isMobile =
            typeof window !== "undefined" && window.innerWidth < 768;
        if (!isMobile) return;
        autoplayRef.current = window.setInterval(() => {
            if (isPausedRef.current) return;
            const current = getNearestIndex();
            const children = getChildren();
            if (children.length === 0) return;
            const next = (current + 1) % children.length;
            scrollToIndex(next);
        }, 3000);
    };

    const stopAutoplay = () => {
        if (autoplayRef.current) {
            window.clearInterval(autoplayRef.current);
            autoplayRef.current = null;
        }
    };

    React.useEffect(() => {
        startAutoplay();
        const onResize = () => startAutoplay();
        window.addEventListener("resize", onResize);
        return () => {
            stopAutoplay();
            window.removeEventListener("resize", onResize);
        };
    }, []);

    const handleTouchStart = () => {
        isPausedRef.current = true;
    };
    const handleTouchEnd = () => {
        isPausedRef.current = false;
    };
    const handleMouseEnter = () => {
        isPausedRef.current = true;
    };
    const handleMouseLeave = () => {
        isPausedRef.current = false;
    };

    return (
        <section
            id="services"
            className="pt-16 md:pt-24 bg-gray-50 relative scroll-mt-32"
        >
            <div className="container mx-auto container-padding ">
                <h2 className="text-3xl font-bold text-center ">
                    Our Services
                </h2>
                <div className="w-32 h-0.5 bg-[#FFD700] mx-auto mb-12 mt-1 "></div>

                {/* Scroll Buttons */}
                <button
                    onClick={() => scrollByCards("left")}
                    className="absolute left-24 top-1/2 z-10 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors hidden md:block"
                >
                    <IoChevronBack size={24} />
                </button>
                <button
                    onClick={() => scrollByCards("right")}
                    className="absolute right-24 top-1/2 z-10 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors hidden md:block"
                >
                    <IoChevronForward size={24} />
                </button>

                {/* Scrollable Container */}
                <div
                    ref={scrollContainerRef}
                    className="flex overflow-x-auto gap-8 pb-4 scrollbar-hide scroll-smooth snap-x snap-mandatory overscroll-x-contain"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {serviceData.map((service, index) => (
                        <ServiceCard
                            key={service.id}
                            {...service}
                            index={index}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Services;
